import React from "react"
import { graphql } from "gatsby"
import Layout from "../components/layout"
import Seo from "../components/seo"
import Banner from "../components/banner"
import Cta from "../components/cta"
import { formatDate } from "../utils/event"
import Arrow from "../components/icons/arrow"
import { siteMetadata } from "../../gatsby-config"
import EventSections from "../components/events/event-sections"
import Breadcrumb from "../components/breadcrumb"
import { kebabCase } from "lodash"
import StyledImage from "../components/styled-image"
import { InformationCircleIcon } from "@heroicons/react/24/outline"

const EventTemplate = ({ location, data: { page, previous, next } }) => {
  const { title, uri, content, seo, featuredImage, eventInformation, categories } = page
  const allDay = eventInformation?.allDay
  const startDateRaw = eventInformation?.timeDate?.startDate
  const startTime = eventInformation?.timeDate?.startTime
  const endDateRaw = eventInformation?.timeDate?.endDate
  const endTime = eventInformation?.timeDate?.endTime
  const sections = eventInformation?.sections

  const previousEventInformation = previous?.eventInformation
  const allDayPrevious = previousEventInformation?.allDay
  const startDateRawPrevious = previousEventInformation?.timeDate?.startDate
  const startTimePrevious = previousEventInformation?.timeDate?.startTime
  const endDateRawPrevious = previousEventInformation?.timeDate?.endDate
  const endTimePrevious = previousEventInformation?.timeDate?.endTime

  const nextEventInformation = next?.eventInformation
  const allDayNext = nextEventInformation?.allDay
  const startDateRawNext = nextEventInformation?.timeDate?.startDate
  const startTimeNext = nextEventInformation?.timeDate?.startTime
  const endDateRawNext = nextEventInformation?.timeDate?.endDate
  const endTimeNext = nextEventInformation?.timeDate?.endTime

  const formattedDate = formatDate(startDateRaw, startTime, endDateRaw, endTime, allDay)
  const formattedDatePrevious = formatDate(startDateRawPrevious, startTimePrevious, endDateRawPrevious, endTimePrevious, allDayPrevious, "startDate")
  const formattedDateNext = formatDate(startDateRawNext, startTimeNext, endDateRawNext, endTimeNext, allDayNext, "startDate")

  // check if event is in the past
  const startDate = startDateRaw ? new Date(`${startDateRaw?.substring(0, 4)}-${startDateRaw?.substring(4, 6)}-${startDateRaw?.substring(6, 8)}${allDay ? "" : ` ${startTime}`}`) : null
  const endDate = endDateRaw ? new Date(`${endDateRaw?.substring(0, 4)}-${endDateRaw?.substring(4, 6)}-${endDateRaw?.substring(6, 8)}${allDay ? "" : ` ${endTime}`}`) : null
  var now = new Date()
  const isPast = endDate ? endDate < now : startDate ? startDate < now : false

  const cost = page?.eventInformation?.cost
  const url = page?.eventInformation?.url
  const urlLabel = page?.eventInformation?.urlLabel

  const cta = url ? {
    title: urlLabel || new URL(url).hostname,
    url: url,
  } : null
  const primaryCategory = categories?.nodes?.length ? categories?.nodes[0]?.name : null
  const banner = {
    heading: title,
    subheading: formattedDate + (cost ? ` | $${cost}` : ""),
    breadcrumb: {
      title: primaryCategory ? primaryCategory : "Entertainment",
      url: "/entertainment/",
    },
    image: featuredImage?.node,
    cta: cta,
    background: primaryCategory ? "white" : "rollingHills"
  }


  return (
    <Layout uri={uri} location={location}>
      <div itemScope itemType="https://schema.org/Event">
        <Seo
          title={seo?.title || title}
          description={seo?.metaDesc || content || `Join us for ${title} at Jamul Casino Resort San Diego on ${formattedDate}.`}
          image={seo?.opengraphImage?.localFile?.publicURL}
          uri={page.uri}
        />
        {isPast ?
          <section className="py-4 bg-green text-white text-center font-bold">
            <p className="container">
              <InformationCircleIcon className="inline-block w-6 h-6 mr-2 -mt-1" />
              This event has passed.
            </p>
          </section>
          : null}
        {primaryCategory === "Special Event" ?
          <section className="py-5 text-center container">
            <Breadcrumb breadcrumb={banner.breadcrumb} background={banner.background} />
            {title ? (
              <h1 id={kebabCase(title)} itemProp="name" className={`mb-4 ${title.length < 36 ? "h1-lg" : ""}`} dangerouslySetInnerHTML={{
                __html: title,
              }} />) : null}
            {banner.image ? <StyledImage
              image={banner.image}
              objectFit="cover"
              objectPosition={"center"}
              className="max-w-3xl mx-auto" /> : null}
            <div className="max-w-3xl mx-auto mt-5">
              {banner.subheading ? (
                <h2 className={`mb-3`} dangerouslySetInnerHTML={{
                  __html: banner.subheading,
                }} />
              ) : null}
              <EventContent page={page} />
              {cta && (
                <Cta props={cta} className={`btn btn-primary`} />
              )}
            </div>
          </section>
          : <Banner props={banner} index={0}>
            <EventContent page={page} />
          </Banner>}
        {sections?.length ? <EventSections props={eventInformation} /> : null}
        <section className="bg-banner-gradient/10 py-12">
          <nav className="container relative">
            <ul
              className="grid w-full sm:grid-cols-2 gap-6 sm:justify-between list-none font-medium"
            >
              <li>
                {previous && (
                  <>
                    <p className="font-semibold sm:hidden">Previous Event:</p>
                    <Cta props={{ url: previous.uri }} rel="prev">
                      <>
                        <Arrow className="hidden sm:inline flex-shrink-0 rotate-180  mr-1 -mt-0.5" />
                        <span className="inline-flex flex-col">
                          {previous.title}
                          <br /><span className="text-body font-normal">{formattedDatePrevious}</span>
                        </span>
                        <Arrow className="inline sm:hidden flex-shrink-0" />
                      </></Cta>
                  </>
                )}
              </li>

              <li className="sm:text-right">
                {next && (
                  <>
                    <p className="font-semibold sm:hidden">Next Event:</p>
                    <Cta props={{ url: next.uri }} rel="next">
                      <>
                        <span className="inline-flex flex-col">
                          {next.title}
                          <br />
                          <span className="text-body font-normal">{formattedDateNext}</span>
                        </span>
                        <Arrow className="flex-shrink-0 inline" />
                      </>
                    </Cta>
                  </>
                )}
              </li>
            </ul>

            <div className="flex sm:justify-center pt-8 sm:pt-2">
              <Cta arrow={true} className="font-semibold btn-text" props={{ url: "/entertainment/", title: "More Entertainment" }} />
            </div>
          </nav>
        </section>
      </div>
    </Layout >
  )
}

export default EventTemplate


function EventContent({ page }) {
  const { content, eventInformation } = page
  const venues = page?.venues?.nodes?.length ? page?.venues?.nodes?.map((venue) => venue) : []
  const venue = venues?.length ? venues[0] : null
  const organizers = page?.organizers?.nodes?.length ? page?.organizers?.nodes?.map((organizer) => organizer) : []
  const phones = organizers?.map(organizer => organizer?.organizerInformation?.phone)?.filter((phone) => phone != null) || []
  const phonesLength = phones?.length
  const allDay = eventInformation?.allDay
  const startDateRaw = eventInformation?.timeDate?.startDate
  const startTime = eventInformation?.timeDate?.startTime
  const endDateRaw = eventInformation?.timeDate?.endDate
  const endTime = eventInformation?.timeDate?.endTime
  const startDateIso8601 = formatDate(startDateRaw, startTime, endDateRaw, endTime, allDay, "startDateIso8601")
  const endDateIso8601 = formatDate(startDateRaw, startTime, endDateRaw, endTime, allDay, "endDateIso8601")
  return <>
    {content ? <div className="pb-2 parsed-content" itemProp="description" dangerouslySetInnerHTML={{ __html: content }} /> : null}
    <div itemProp="location" itemScope itemType="https://schema.org/Place">
      {venues?.length ?
        <p className="pb-2">Venue:{" "}
          {venues?.length === 1 && venue?.venueInformation?.url ?
            <Cta props={{ url: venue?.venueInformation?.url }} itemProp="url">
              <span itemProp="name" className="font-medium">{venue?.title}</span>
            </Cta>
            : <span className="font-medium" itemProp="name">{venues.map((venue, index) => (
              <span key={index}>{index > 0 && index < venues.length - 1 ? " | " : " "}{venue?.title}</span>
            ))}</span>}
        </p>
        : <span className="sr-only" itemProp="name">Jamul Casino Resort San Diego</span>}
      <span className="sr-only" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
        <span itemProp="streetAddress">{venue?.venueInformation?.address || "14145 Campo Road"}</span>
        <span itemProp="addressLocality">{venue?.venueInformation?.city || "Jamul"}</span>,
        <span itemProp="addressRegion">{venue?.venueInformation?.state || "CA"}</span>
        <span itemProp="postalCode">{venue?.venueInformation?.zip || "91935"}</span>
      </span>
    </div>
    <meta itemProp="startDate" content={startDateIso8601} />
    <meta itemProp="endDate" content={endDateIso8601} />
    {phonesLength ? <p className="pb-2">Phone:{" "}
      {phones.map((phone, index) => (
        <span key={index}>{index > 0 && index < phonesLength - 1 ? " | " : " "}<a href={`tel:${phone}`} className="font-medium">{phone}</a></span>
      ))}
    </p> : null}
    <div className="sr-only" itemProp="organizer" itemScope itemType="https://schema.org/Organization">
      <a itemProp="url" href={`${process.env.SITE_URL || siteMetadata.siteUrl}${venue?.url || ""}`}>
        <span itemProp="name">{`${organizers?.length ? organizers[0]?.title : venue?.title || "Jamul Casino Resort San Diego"}`}</span></a>
    </div>
  </>
}

export const pageQuery = graphql`
  query WordPressEvent($id: String!, $previousId: String, $nextId: String) {
    page: wpMeshEvent(id: {eq: $id }) {
      title
      uri
      content
      seo {
        title
        metaDesc
        opengraphImage {
          localFile {
            publicURL
          }
        }
      }
      featuredImage {
        node {
          altText
          localFile {
            publicURL
            extension
            childImageSharp {
              gatsbyImageData(width: 2560)
            }
          }  
        }
      }
      venues: meshVenues {
        nodes {
          title: name
          venueInformation {
            url: website
            phone
            address
            city
            state
            zip
          }
        }
      }
      organizers: meshOrganizers {
        nodes {
          title: name
          organizerInformation {
            phone
          }
        }
      }
      categories: meshCategories {
        nodes {
          name
        }
      }
      eventInformation {
        timeDate {
          startDate
          startTime
          endDate
          endTime
        }
        allDay
        cost
        url
        urlLabel
        sections {
          ... on WpMeshEvent_Eventinformation_Sections_Banner {
            content
            fieldGroupName
            heading
            image {
              altText
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(width: 2560)
                }
              } 
            }
            imageBorderColor
            links {
              link {
                target
                title
                url
              }
            }
          }
          ... on WpMeshEvent_Eventinformation_Sections_Gallery {
            fieldGroupName
            imageBorderColor
            images: gallery {
              altText
              caption
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(width: 512)
                  original {
                    height
                    src
                    width
                  }
                }
              }            
            }
            overlayImage {
              altText
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(width: 2560)
                }
              }               
            }
          }
        }
      }
    }
    previous: wpMeshEvent(id: {eq: $previousId }) {
      ...EventCard
    }
    next: wpMeshEvent(id: {eq: $nextId }) {
      ...EventCard
    }
  }
`

